import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUser,
	faBook,
	faEye,
	faEyeSlash,
	faTrash,
	faPlus,
	faMinus,
	faCheck,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import Modal from "../../../../components/common/Modal";
import Button from "../../../../components/common/Button";

import {
	useCreateTeacherMutation,
	useUpdateTeacherMutation,
	useAddSubjectToTeacherMutation,
	useRemoveSubjectFromTeacherMutation,
} from "../../../../store/api/teacherApiSlice";

import styles from "./TeacherModal.module.css";

const TeacherModal = ({ teacher, subjects, departments, onClose }) => {
	const [createTeacher, { isLoading: isCreating }] = useCreateTeacherMutation();
	const [updateTeacher, { isLoading: isUpdating }] = useUpdateTeacherMutation();
	const [addSubjectToTeacher] = useAddSubjectToTeacherMutation();
	const [removeSubjectFromTeacher] = useRemoveSubjectFromTeacherMutation();

	const [activeTab, setActiveTab] = useState("profile");
	const [showLoginPassword, setShowLoginPassword] = useState(false);
	const [showGradePassword, setShowGradePassword] = useState(false);

	// Form data
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		loginPassword: "",
		gradePassword: "",
		isAdmin: false,
		subjects: [],
	});

	// Subject management
	const [subjectToAdd, setSubjectToAdd] = useState("");
	const [selectedSubjectForEdit, setSelectedSubjectForEdit] = useState(null);
	const [departmentSelection, setDepartmentSelection] = useState([]);

	// Track which subjects were originally assigned to detect changes
	const [originalSubjects, setOriginalSubjects] = useState([]);
	const [subjectUpdates, setSubjectUpdates] = useState({
		add: [],
		remove: [],
		update: [],
	});

	const isEditing = !!teacher;
	const isLoading = isCreating || isUpdating;

	// Initialize form data
	useEffect(() => {
		if (teacher) {
			// Transform assigned subjects for editing
			const transformedSubjects = [];

			if (teacher.assignedSubjects && Array.isArray(teacher.assignedSubjects)) {
				teacher.assignedSubjects.forEach((assignment) => {
					const subject = subjects.find((s) => s.id === assignment.subjectId);
					if (subject) {
						const departmentIdArray = Array.isArray(assignment.departmentId)
							? [...assignment.departmentId]
							: [assignment.departmentId];

						const classes = departmentIdArray.map((depId) => {
							const department = departments.find((d) => d.id === depId);
							return department ? department.departmentName : depId;
						});

						transformedSubjects.push({
							id: subject.id,
							name: subject.name,
							classes: [...classes],
							departmentIds: [...departmentIdArray],
						});
					}
				});
			}

			setFormData({
				firstName: teacher.firstName || "",
				lastName: teacher.lastName || "",
				email: teacher.email || "",
				loginPassword: "",
				gradePassword: "",
				isAdmin: teacher.isAdmin || false,
				subjects: [...transformedSubjects],
			});

			setOriginalSubjects(
				transformedSubjects.map((subject) => ({
					...subject,
					departmentIds: [...subject.departmentIds],
					classes: [...subject.classes],
				}))
			);
			setSubjectUpdates({ add: [], remove: [], update: [] });
		} else {
			setFormData({
				firstName: "",
				lastName: "",
				email: "",
				loginPassword: "",
				gradePassword: "",
				isAdmin: false,
				subjects: [],
			});
			setOriginalSubjects([]);
		}

		setSubjectToAdd("");
		setSelectedSubjectForEdit(null);
		setDepartmentSelection([]);
		setActiveTab("profile");
	}, [teacher, subjects, departments]);

	// Form handlers
	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const getAvailableSubjects = () => {
		return subjects.filter(
			(subject) => !formData.subjects.some((s) => s.id === subject.id)
		);
	};

	const handleAddSubject = (subjectId) => {
		const subject = subjects.find((s) => s.id === subjectId);
		if (!subject) return;

		const newSubject = {
			id: subject.id,
			name: subject.name,
			classes: [],
			departmentIds: [],
		};

		setFormData((prev) => ({
			...prev,
			subjects: [...prev.subjects, newSubject],
		}));

		setSubjectToAdd("");
		setSelectedSubjectForEdit(subject.id);

		if (isEditing) {
			setSubjectUpdates((prev) => ({
				...prev,
				add: [...prev.add, { subjectId: subject.id, departmentIds: [] }],
			}));
		}
	};

	const handleRemoveSubject = (subjectId) => {
		setFormData((prev) => ({
			...prev,
			subjects: prev.subjects.filter((s) => s.id !== subjectId),
		}));

		if (selectedSubjectForEdit === subjectId) {
			setSelectedSubjectForEdit(null);
			setDepartmentSelection([]);
		}

		if (isEditing) {
			const wasOriginal = originalSubjects.some((s) => s.id === subjectId);

			if (wasOriginal) {
				setSubjectUpdates((prev) => ({
					...prev,
					remove: [...prev.remove, subjectId],
					add: prev.add.filter((item) => item.subjectId !== subjectId),
				}));
			} else {
				setSubjectUpdates((prev) => ({
					...prev,
					add: prev.add.filter((item) => item.subjectId !== subjectId),
				}));
			}
		}
	};

	const handleDepartmentToggle = (departmentId) => {
		if (!selectedSubjectForEdit) return;

		const department = departments.find((d) => d.id === departmentId);
		if (!department) return;

		let newSelection = [...departmentSelection];

		if (newSelection.includes(departmentId)) {
			newSelection = newSelection.filter((id) => id !== departmentId);
		} else {
			newSelection.push(departmentId);
		}

		setDepartmentSelection(newSelection);

		setFormData((prev) => ({
			...prev,
			subjects: prev.subjects.map((subject) =>
				subject.id === selectedSubjectForEdit
					? {
							...subject,
							classes: newSelection.map((depId) => {
								const dept = departments.find((d) => d.id === depId);
								return dept ? dept.departmentName : depId;
							}),
							departmentIds: [...newSelection],
					  }
					: subject
			),
		}));

		if (isEditing) {
			const originalSubject = originalSubjects.find(
				(s) => s.id === selectedSubjectForEdit
			);
			const wasOriginal = !!originalSubject;

			if (wasOriginal) {
				const originalDepartmentIds = [...originalSubject.departmentIds].sort();
				const newDepartmentIds = [...newSelection].sort();
				const departmentsChanged =
					JSON.stringify(originalDepartmentIds) !==
					JSON.stringify(newDepartmentIds);

				if (departmentsChanged) {
					setSubjectUpdates((prev) => ({
						...prev,
						update: prev.update
							.filter((item) => item.subjectId !== selectedSubjectForEdit)
							.concat([
								{
									subjectId: selectedSubjectForEdit,
									departmentIds: [...newSelection],
								},
							]),
					}));
				}
			} else {
				setSubjectUpdates((prev) => ({
					...prev,
					add: prev.add.map((item) =>
						item.subjectId === selectedSubjectForEdit
							? { ...item, departmentIds: [...newSelection] }
							: item
					),
				}));
			}
		}
	};

	const handleSubjectSelect = (subjectId) => {
		setSelectedSubjectForEdit(subjectId);
		const subject = formData.subjects.find((s) => s.id === subjectId);
		setDepartmentSelection(subject ? [...subject.departmentIds] : []);
	};

	const handleSave = async () => {
		try {
			// Validation
			if (
				!formData.firstName.trim() ||
				!formData.lastName.trim() ||
				!formData.email.trim()
			) {
				alert("Please fill in all required fields.");
				return;
			}

			if (!isEditing && !formData.loginPassword.trim()) {
				alert("Login password is required for new teachers.");
				return;
			}

			const teacherData = {
				firstName: formData.firstName.trim(),
				lastName: formData.lastName.trim(),
				email: formData.email.trim(),
				isAdmin: formData.isAdmin,
			};

			if (activeTab === "profile") {
				if (formData.loginPassword.trim()) {
					teacherData.loginPassword = formData.loginPassword.trim();
				}
				if (formData.gradePassword.trim()) {
					teacherData.gradePassword = formData.gradePassword.trim();
				}
			}

			if (isEditing) {
				if (activeTab === "profile") {
					await updateTeacher({
						id: teacher.id,
						...teacherData,
					}).unwrap();
				}

				// Handle subject updates
				for (const subjectId of subjectUpdates.remove) {
					await removeSubjectFromTeacher({
						teacherId: teacher.id,
						subjectId: subjectId,
					}).unwrap();
				}

				for (const subjectUpdate of subjectUpdates.add) {
					if (subjectUpdate.departmentIds.length > 0) {
						await addSubjectToTeacher({
							teacherId: teacher.id,
							subjectId: subjectUpdate.subjectId,
							departmentIds: subjectUpdate.departmentIds,
						}).unwrap();
					}
				}

				for (const subjectUpdate of subjectUpdates.update) {
					await removeSubjectFromTeacher({
						teacherId: teacher.id,
						subjectId: subjectUpdate.subjectId,
					}).unwrap();

					if (subjectUpdate.departmentIds.length > 0) {
						await addSubjectToTeacher({
							teacherId: teacher.id,
							subjectId: subjectUpdate.subjectId,
							departmentIds: subjectUpdate.departmentIds,
						}).unwrap();
					}
				}
			} else {
				const newTeacher = await createTeacher(teacherData).unwrap();

				for (const subject of formData.subjects) {
					if (subject.departmentIds.length > 0) {
						await addSubjectToTeacher({
							teacherId: newTeacher.id,
							subjectId: subject.id,
							departmentIds: subject.departmentIds,
						}).unwrap();
					}
				}
			}

			onClose();
		} catch (error) {
			console.error("Error saving teacher:", error);
			const errorMessage =
				error?.data?.message ||
				error?.message ||
				"Failed to save teacher. Please try again.";
			alert(`Error: ${errorMessage}`);
		}
	};

	const handleDelete = async () => {
		if (!isEditing) return;

		if (
			window.confirm(
				`Are you sure you want to delete ${formData.firstName} ${formData.lastName}?`
			)
		) {
			try {
				onClose();
			} catch (error) {
				console.error("Error deleting teacher:", error);
				alert("Failed to delete teacher. Please try again.");
			}
		}
	};

	const modalTitle = isEditing
		? `${formData.firstName} ${formData.lastName}`
		: "Add New Teacher";

	const footer = (
		<>
			{isEditing && (
				<Button variant="danger" onClick={handleDelete} className="me-auto">
					<FontAwesomeIcon icon={faTrash} /> Delete
				</Button>
			)}
			<Button variant="secondary" onClick={onClose}>
				Cancel
			</Button>
			<Button variant="primary" onClick={handleSave} disabled={isLoading}>
				{isLoading
					? "Saving..."
					: isEditing
					? "Save Changes"
					: "Create Teacher"}
			</Button>
		</>
	);

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={modalTitle}
			size="large"
			footer={footer}
			loading={isLoading}
			className={styles.teacherModal}
		>
			<div className={styles.modalLayout}>
				{/* Sidebar Navigation */}
				<div className={styles.sidebar}>
					<nav className={styles.tabNavigation}>
						<button
							type="button"
							className={`${styles.navItem} ${
								activeTab === "profile" ? styles.active : ""
							}`}
							onClick={() => setActiveTab("profile")}
						>
							<span className={styles.navIcon}>
								<FontAwesomeIcon icon={faUser} />
							</span>
							<span className={styles.navText}>Profile</span>
							{activeTab === "profile" && (
								<FontAwesomeIcon
									icon={faChevronRight}
									className={styles.indicator}
								/>
							)}
						</button>
						<button
							type="button"
							className={`${styles.navItem} ${
								activeTab === "subjects" ? styles.active : ""
							}`}
							onClick={() => setActiveTab("subjects")}
						>
							<span className={styles.navIcon}>
								<FontAwesomeIcon icon={faBook} />
							</span>
							<span className={styles.navText}>Subjects & Classes</span>
							{activeTab === "subjects" && (
								<FontAwesomeIcon
									icon={faChevronRight}
									className={styles.indicator}
								/>
							)}
						</button>
					</nav>
				</div>

				{/* Content Area */}
				<div className={styles.contentArea}>
					{activeTab === "profile" && (
						<div className={styles.profileTab}>
							{/* Personal Information Section */}
							<div className={styles.formSection}>
								<h3>Personal Information</h3>
								<div className={styles.formGrid}>
									<div className={styles.formGroup}>
										<label>First Name *</label>
										<input
											type="text"
											value={formData.firstName}
											onChange={(e) =>
												handleInputChange("firstName", e.target.value)
											}
											placeholder="Enter first name"
											className={styles.input}
										/>
									</div>
									<div className={styles.formGroup}>
										<label>Last Name *</label>
										<input
											type="text"
											value={formData.lastName}
											onChange={(e) =>
												handleInputChange("lastName", e.target.value)
											}
											placeholder="Enter last name"
											className={styles.input}
										/>
									</div>
									<div className={styles.formGroup}>
										<label>Email Address *</label>
										<input
											type="email"
											value={formData.email}
											onChange={(e) =>
												handleInputChange("email", e.target.value)
											}
											placeholder="email@school.edu"
											className={styles.input}
										/>
									</div>
									<div className={styles.adminToggle}>
										<label>Administrator Access</label>
										<div className={styles.toggleContainer}>
											<label className={styles.switch}>
												<input
													type="checkbox"
													checked={formData.isAdmin}
													onChange={() =>
														handleInputChange("isAdmin", !formData.isAdmin)
													}
												/>
												<span className={styles.slider}></span>
											</label>
											<span className={styles.toggleLabel}>
												{formData.isAdmin ? "Admin" : "Teacher"}
											</span>
										</div>
									</div>
								</div>
							</div>

							{/* Security Section */}
							<div className={styles.formSection}>
								<h3>Security</h3>
								<div className={styles.formGrid}>
									<div className={styles.formGroup}>
										<label>Login Password {!isEditing && "*"}</label>
										<div className={styles.passwordField}>
											<input
												type={showLoginPassword ? "text" : "password"}
												value={formData.loginPassword}
												onChange={(e) =>
													handleInputChange("loginPassword", e.target.value)
												}
												placeholder={
													isEditing
														? "Leave blank to keep current"
														: "Enter password"
												}
												className={styles.input}
											/>
											<button
												type="button"
												className={styles.passwordToggle}
												onClick={() => setShowLoginPassword(!showLoginPassword)}
											>
												<FontAwesomeIcon
													icon={showLoginPassword ? faEyeSlash : faEye}
												/>
											</button>
										</div>
									</div>
									<div className={styles.formGroup}>
										<label>Grade Password</label>
										<div className={styles.passwordField}>
											<input
												type={showGradePassword ? "text" : "password"}
												value={formData.gradePassword}
												onChange={(e) =>
													handleInputChange("gradePassword", e.target.value)
												}
												placeholder={
													isEditing
														? "Leave blank to keep current"
														: "Enter password (optional)"
												}
												className={styles.input}
											/>
											<button
												type="button"
												className={styles.passwordToggle}
												onClick={() => setShowGradePassword(!showGradePassword)}
											>
												<FontAwesomeIcon
													icon={showGradePassword ? faEyeSlash : faEye}
												/>
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{activeTab === "subjects" && (
						<div className={styles.subjectsTab}>
							<div className={styles.subjectsLayout}>
								{/* Left: Subject List */}
								<div className={styles.subjectPanel}>
									<div className={styles.panelHeader}>
										<h3>Assigned Subjects</h3>
									</div>

									<div className={styles.addSubjectSection}>
										<div className={styles.addSubjectRow}>
											<select
												value={subjectToAdd}
												onChange={(e) => setSubjectToAdd(e.target.value)}
												className={styles.subjectSelect}
											>
												<option value="">+ Add Subject</option>
												{getAvailableSubjects().map((subject) => (
													<option key={subject.id} value={subject.id}>
														{subject.name}
													</option>
												))}
											</select>
											{subjectToAdd && (
												<button
													type="button"
													className={`btn btn-primary btn-sm ${styles.addButton}`}
													onClick={() => handleAddSubject(subjectToAdd)}
												>
													<FontAwesomeIcon icon={faPlus} />
												</button>
											)}
										</div>
									</div>

									<div className={styles.subjectList}>
										{formData.subjects.length === 0 ? (
											<div className={styles.emptyState}>
												<p>No subjects assigned</p>
											</div>
										) : (
											<div className={styles.subjectItems}>
												{formData.subjects.map((subject) => (
													<div
														key={subject.id}
														className={`${styles.subjectItem} ${
															selectedSubjectForEdit === subject.id
																? styles.selected
																: ""
														}`}
														onClick={() => handleSubjectSelect(subject.id)}
													>
														<div className={styles.subjectInfo}>
															<div className={styles.subjectName}>
																{subject.name}
															</div>
															<div className={styles.subjectMeta}>
																{subject.classes.length} classes
															</div>
														</div>
														<button
															type="button"
															className={styles.removeButton}
															onClick={(e) => {
																e.stopPropagation();
																handleRemoveSubject(subject.id);
															}}
														>
															<FontAwesomeIcon icon={faMinus} />
														</button>
													</div>
												))}
											</div>
										)}
									</div>
								</div>

								{/* Right: Class Assignment */}
								<div className={styles.classPanel}>
									<div className={styles.panelHeader}>
										<h3>Assign Classes</h3>
									</div>

									{selectedSubjectForEdit ? (
										<>
											<div className={styles.selectedSubject}>
												<h4>
													{
														formData.subjects.find(
															(s) => s.id === selectedSubjectForEdit
														)?.name
													}
												</h4>
												<p>
													Select which classes this teacher will teach for this
													subject
												</p>
											</div>

											<div className={styles.classGrid}>
												{departments.map((department) => {
													const isSelected = departmentSelection.includes(
														department.id
													);
													return (
														<div
															key={department.id}
															className={`${styles.classItem} ${
																isSelected ? styles.selected : ""
															}`}
															onClick={() =>
																handleDepartmentToggle(department.id)
															}
														>
															<span>{department.departmentName}</span>
															{isSelected && (
																<FontAwesomeIcon
																	icon={faCheck}
																	className={styles.checkIcon}
																/>
															)}
														</div>
													);
												})}
											</div>
										</>
									) : (
										<div className={styles.emptyState}>
											<p>Select a subject to assign classes</p>
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default TeacherModal;
